import React, { useState, useMemo, useRef, useEffect } from "react";
import { Plus, Trash2, Sparkles, Award, Leaf, Upload, Loader2, Trophy, HelpCircle } from "lucide-react";
import { useData } from "@/context/DataContext";
import { PageHeader, EventSelector, NoEvent, KpiCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { euro } from "@/lib/format";
import client from "@/lib/api";
import { toast } from "sonner";
import { PROVIDER_TYPES, PROVIDER_TYPE_HINTS } from "@/lib/eventConstants";

const CATEGORIES = PROVIDER_TYPES;
const PRIORITY_WEIGHTS = { high: 40, med: 20, low: 10, off: 0 };
const PRIORITY_LABELS = { high: "🔥 Priorité haute", med: "⭐ Important", low: "💡 Optionnel", off: "🚫 Ignorer" };
const DEFAULT_PRIOS = { tarif: "high", proximite: "low", disponibilite: "med", note: "med", rse: "low", acompte: "low", qp: "high" };
const CRITERIA = [
  ["tarif", "Tarif", "Prestataires les moins chers en priorité"],
  ["proximite", "Proximité (km)", "Favorise les prestataires locaux"],
  ["disponibilite", "Disponibilité aux dates", "Prestataires disponibles = +100 points"],
  ["note", "Note moyenne / avis", "Prestataires les mieux notés"],
  ["rse", "Label RSE / éco-responsable", "Bonus si engagement RSE"],
  ["acompte", "% Acompte demandé", "Favorise les faibles acomptes"],
  ["qp", "Rapport qualité / prix", "Note d'équilibre qualité-prix"],
];
const EMPTY_Q = { name: "", category: "Traiteur", price: 0, distance: 10, availability: true, rating: 7.0, rse: false, acomptePct: 30, qualityPrice: 7.0 };

/* Enumerate all combinations (one row per category present), pick best under budget */
function bestCombination(rows, weights, budget) {
  if (!rows.length) return null;
  const byCat = {};
  rows.forEach((r) => { (byCat[r.category] = byCat[r.category] || []).push(r); });
  const cats = Object.keys(byCat);
  // Normalize criteria on the full pool
  const maxPrice = Math.max(...rows.map((r) => r.price)) || 1;
  const maxDist = Math.max(...rows.map((r) => r.distance)) || 1;
  const scoreOf = (r) => {
    const s = {
      tarif: 100 * (1 - r.price / maxPrice),
      proximite: 100 * (1 - r.distance / maxDist),
      disponibilite: r.availability ? 100 : 0,
      note: (Number(r.rating) / 10) * 100,
      rse: r.rse ? 100 : 40,
      acompte: 100 * (1 - Number(r.acomptePct) / 100),
      qp: (Number(r.qualityPrice) / 10) * 100,
    };
    const tot = Object.entries(weights).reduce((a, [k, w]) => a + (s[k] || 0) * (Number(w) / 100), 0);
    return { subScores: s, score: tot };
  };
  const scored = rows.map((r) => ({ ...r, ...scoreOf(r) }));
  const catLists = cats.map((c) => scored.filter((r) => r.category === c));

  // Cartesian product with pruning
  let best = null, worst = null;
  const rec = (i, picks, cost, sumScore) => {
    if (i === catLists.length) {
      const avg = sumScore / picks.length;
      if (cost <= budget && (!best || avg > best.avg)) best = { picks: [...picks], cost, avg };
      if (!worst || cost > worst.cost) worst = { picks: [...picks], cost, avg };
      return;
    }
    for (const p of catLists[i]) {
      picks.push(p);
      rec(i + 1, picks, cost + p.price, sumScore + p.score);
      picks.pop();
    }
  };
  rec(0, [], 0, 0);
  return { scored, best, worst, cats };
}

export default function Devis() {
  const { selectedEvent, expensesByEvent } = useData();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_Q);
  const [prios, setPrios] = useState(DEFAULT_PRIOS);
  const [showGuide, setShowGuide] = useState(false);
  const [showCategoryGuide, setShowCategoryGuide] = useState(false);
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!selectedEvent) return;
    setLoaded(false); setResult(null);
    client.get(`/devis/${selectedEvent.id}`).then(({ data }) => {
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setPrios(data?.priorities && Object.keys(data.priorities).length ? { ...DEFAULT_PRIOS, ...data.priorities } : DEFAULT_PRIOS);
      setLoaded(true);
      setDirty(false);
    }).catch(() => { setRows([]); setPrios(DEFAULT_PRIOS); setLoaded(true); });
  }, [selectedEvent]);

  // Auto-save (debounced) when rows or priorities change
  useEffect(() => {
    if (!loaded || !selectedEvent) return;
    if (!dirty) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await client.put(`/devis/${selectedEvent.id}`, { rows, priorities: prios });
        setDirty(false);
      } catch (e) { /* silent */ } finally { setSaving(false); }
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [rows, prios, dirty, loaded, selectedEvent]);

  if (!selectedEvent) return (<><PageHeader title="Comparateur de devis IA" /><NoEvent /></>);
  const markDirty = () => setDirty(true);

  // Convert priorities to normalized weights (%)
  const rawW = Object.fromEntries(Object.entries(prios).map(([k, p]) => [k, PRIORITY_WEIGHTS[p] || 0]));
  const totalW = Object.values(rawW).reduce((a, b) => a + b, 0);
  const normW = totalW > 0 ? Object.fromEntries(Object.entries(rawW).map(([k, v]) => [k, (v / totalW) * 100])) : rawW;

  const budget = Number(selectedEvent.budget || 0);
  const spent = (expensesByEvent(selectedEvent.id) || []).reduce((a, x) => a + Number(x.amount || 0), 0);
  const remainingBudget = Math.max(0, budget - spent);

  const addRow = () => {
    if (!form.name || !form.price) { toast.error("Nom et prix obligatoires"); return; }
    setRows((r) => [...r, { ...form, id: `q_${Date.now()}`, price: Number(form.price), distance: Number(form.distance), rating: Number(form.rating), acomptePct: Number(form.acomptePct), qualityPrice: Number(form.qualityPrice) }]);
    setForm(EMPTY_Q); markDirty(); toast.success("Devis ajouté");
  };
  const delRow = (id) => { setRows((r) => r.filter((x) => x.id !== id)); markDirty(); };

  const setPrio = (k, v) => { setPrios({ ...prios, [k]: v }); markDirty(); };

  const onImportFile = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await client.post("/ai/parse-devis", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const cat = CATEGORIES.includes(data.category) ? data.category : "Traiteur";
      const newRow = {
        id: `q_${Date.now()}`,
        name: (data.name || file.name.replace(/\.[^.]+$/, "")).slice(0, 60),
        category: cat,
        price: Number(data.price || 0),
        distance: Number(data.distance || 10),
        availability: data.availability !== false,
        rating: Number(data.rating || 7),
        rse: !!data.rse,
        acomptePct: Number(data.acomptePct || 30),
        qualityPrice: Number(data.qualityPrice || 7),
      };
      setRows((r) => [...r, newRow]);
      markDirty();
      toast.success(`✅ Devis "${newRow.name}" importé — ${euro(newRow.price)} · ${cat}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lecture du devis impossible");
    } finally { setImporting(false); ev.target.value = ""; }
  };

  const analyze = () => {
    if (rows.length === 0) { toast.error("Ajoutez au moins un devis"); return; }
    const b = remainingBudget > 0 ? remainingBudget : budget;
    const res = bestCombination(rows, normW, b);
    if (!res?.best) {
      toast.error("Aucune combinaison ne rentre dans le budget disponible");
      setResult({ ...res, budget: b, noFit: true });
      return;
    }
    const savings = Math.max(0, (res.worst?.cost || 0) - res.best.cost);
    setResult({ ...res, budget: b, savings, globalScore: Math.round(res.best.avg) });
    toast.success("Analyse terminée — meilleure combinaison identifiée");
  };

  const applySelection = () => {
    if (!result?.best?.picks?.length) return;
    toast.success(`✅ Sélection appliquée — ${result.best.picks.length} prestataire(s) pré-rempli(s) dans les demandes de devis`);
  };

  return (
    <>
      <PageHeader
        title="Comparateur de devis IA"
        subtitle="Trouvez la meilleure combinaison de prestataires pour votre événement"
        actions={<div className="flex flex-wrap items-center gap-2"><EventSelector /><span className="text-[11px] text-muted-foreground" data-testid="devis-save-status">{saving ? "💾 Enregistrement…" : dirty ? "• Modifications non enregistrées" : loaded ? "✓ Enregistré" : ""}</span><input ref={fileRef} type="file" accept="image/*,application/pdf,.pdf" className="hidden" onChange={onImportFile} data-testid="devis-import-input" /><Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="devis-import"><span className="flex items-center gap-1">{importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Importer un devis (PDF/Image)</span></Button></div>}
      />

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <KpiCard tint="blue" label="Budget total" value={euro(budget)} />
        <KpiCard tint="amber" label="Budget disponible (Budget − Dépenses)" value={euro(remainingBudget)} sub={`Dépensé: ${euro(spent)}`} />
        <KpiCard tint="emerald" label="Devis à comparer" value={rows.length} />
      </div>

      <div className="bg-card rounded-xl border border-border p-5 mb-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-display font-semibold flex items-center gap-2"><Award className="h-4 w-4 text-indigo-600" />Vos priorités pour choisir les prestataires</h3>
          <button type="button" onClick={() => setShowGuide((v) => !v)} className="text-xs text-indigo-600 underline flex items-center gap-1" data-testid="devis-guide-toggle"><HelpCircle className="h-3.5 w-3.5" />{showGuide ? "Masquer" : "Comment ça marche ?"}</button>
        </div>
        {showGuide && (
          <div className="mb-4 p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-xs text-indigo-900 space-y-1.5" data-testid="devis-guide">
            <p><b>Comment fonctionne le comparateur ?</b></p>
            <p>1️⃣ Pour chaque critère (Tarif, Note, RSE…), choisissez son <b>importance</b> pour vous : <b>Priorité haute</b> (compte beaucoup), <b>Important</b>, <b>Optionnel</b> ou <b>Ignorer</b>.</p>
            <p>2️⃣ Ajoutez vos devis (à la main ou en important un PDF/image).</p>
            <p>3️⃣ Cliquez sur <b>« Lancer l'analyse IA »</b>. L'algorithme teste <b>toutes les combinaisons possibles</b> (1 prestataire par catégorie) et retient celle au meilleur score sous votre budget disponible.</p>
            <p>💡 <i>Astuce</i> : mettez « Priorité haute » sur ce qui compte vraiment (souvent Tarif + Q/P), « Ignorer » ce qui vous est indifférent — vous n'avez pas à équilibrer des pourcentages, JustPlanIt s'en charge.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {CRITERIA.map(([k, label, hint]) => (
            <div key={k} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-slate-50 border border-slate-100" data-testid={`prio-row-${k}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{label}</p>
                <p className="text-[11px] text-muted-foreground truncate">{hint}</p>
              </div>
              <Select value={prios[k]} onValueChange={(v) => setPrio(k, v)}>
                <SelectTrigger className="w-40 h-8 text-xs" data-testid={`prio-${k}`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY_LABELS).map(([p, l]) => <SelectItem key={p} value={p}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <span className="font-mono text-xs text-muted-foreground w-12 text-right shrink-0">{Math.round(normW[k] || 0)}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <Button type="button" variant="outline" size="sm" onClick={() => setShowCategoryGuide((shown) => !shown)} data-testid="devis-category-guide-toggle">
          <HelpCircle className="h-4 w-4 mr-1.5" />Repères de catégorisation
        </Button>
        {showCategoryGuide && <div className="mt-3 border border-sky-200 bg-sky-50 p-4 rounded-xl" data-testid="devis-category-guide">
          <p className="text-sm font-semibold text-sky-900 mb-2">Repères de catégorisation</p>
          <ul className="text-xs text-sky-800 space-y-1">{PROVIDER_TYPE_HINTS.map((hint) => <li key={hint}>• {hint}</li>)}</ul>
        </div>}
      </div>

      <div className="bg-card rounded-xl border border-border p-5 mb-6">
        <h3 className="font-display font-semibold mb-4">Ajouter un devis</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div><Label className="mb-1.5 block text-xs">Nom du prestataire *</Label><Input placeholder="Ex : Traiteur Dupont" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="devis-name" /></div>
          <div><Label className="mb-1.5 block text-xs">Catégorie *</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="devis-cat"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="mb-1.5 block text-xs">Prix (€) *</Label><Input type="number" min="0" placeholder="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="devis-price" /></div>
          <div><Label className="mb-1.5 block text-xs">Distance (km)</Label><Input type="number" min="0" placeholder="10" value={form.distance} onChange={(e) => setForm({ ...form, distance: e.target.value })} data-testid="devis-distance" /></div>
          <div><Label className="mb-1.5 block text-xs">Note /10</Label><Input type="number" step="0.1" min="0" max="10" placeholder="7.0" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="devis-rating" /></div>
          <div><Label className="mb-1.5 block text-xs">% Acompte</Label><Input type="number" min="0" max="100" placeholder="30" value={form.acomptePct} onChange={(e) => setForm({ ...form, acomptePct: e.target.value })} data-testid="devis-acompte" /></div>
          <div><Label className="mb-1.5 block text-xs">Rapport qualité/prix /10</Label><Input type="number" step="0.1" min="0" max="10" placeholder="7.0" value={form.qualityPrice} onChange={(e) => setForm({ ...form, qualityPrice: e.target.value })} data-testid="devis-qp" /></div>
          <div className="flex flex-col justify-end pb-1"><Label className="mb-2 block text-xs">Options</Label>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-1.5"><Checkbox checked={form.availability} onCheckedChange={(v) => setForm({ ...form, availability: !!v })} data-testid="devis-avail" />Disponible</label>
              <label className="flex items-center gap-1.5"><Checkbox checked={form.rse} onCheckedChange={(v) => setForm({ ...form, rse: !!v })} data-testid="devis-rse" />Label RSE</label>
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-3"><Button onClick={addRow} data-testid="devis-add"><Plus className="h-4 w-4 mr-1" />Ajouter</Button></div>
      </div>

      <div className="bg-card rounded-xl border border-border p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold">{rows.length} devis — {new Set(rows.map((r) => r.category)).size} catégorie(s)</h3>
          <Button onClick={analyze} disabled={!rows.length} data-testid="devis-analyze"><Sparkles className="h-4 w-4 mr-1" />Lancer l'analyse IA</Button>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Catégorie</TableHead><TableHead>Prix</TableHead><TableHead>Distance</TableHead><TableHead>Note</TableHead><TableHead>RSE</TableHead><TableHead>Acompte</TableHead><TableHead>Q/P</TableHead><TableHead>Score</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length ? rows.map((r) => {
              const sc = result?.scored?.find((s) => s.id === r.id);
              const isChosen = result?.best?.picks?.some((c) => c.id === r.id);
              return (
                <TableRow key={r.id} className={isChosen ? "bg-emerald-50" : ""} data-testid={`devis-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}{isChosen && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-200 text-emerald-800">Recommandé</span>}</TableCell>
                  <TableCell>{r.category}</TableCell>
                  <TableCell>{euro(r.price)}</TableCell>
                  <TableCell>{r.distance} km</TableCell>
                  <TableCell>{r.rating}/10</TableCell>
                  <TableCell>{r.rse ? <Leaf className="h-4 w-4 text-emerald-600" /> : "—"}</TableCell>
                  <TableCell>{r.acomptePct}%</TableCell>
                  <TableCell>{r.qualityPrice}/10</TableCell>
                  <TableCell className="font-mono">{sc ? Math.round(sc.score) : "—"}</TableCell>
                  <TableCell><Button size="icon" variant="ghost" onClick={() => delRow(r.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button></TableCell>
                </TableRow>
              );
            }) : <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6">Aucun devis. Ajoutez-en un manuellement ou importez un PDF/image.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {result && result.noFit && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-5" data-testid="devis-nofit">
          <p className="text-sm text-rose-800">Aucune combinaison ne rentre dans le budget disponible ({euro(result.budget)}). Ajoutez des devis moins chers, ajustez les pondérations ou augmentez le budget.</p>
        </div>
      )}

      {result && result.best && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5" data-testid="devis-result">
          <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Trophy className="h-5 w-5 text-emerald-700" />Combinaison gagnante</h3>
          <p className="text-sm mb-3 font-medium">{result.best.picks.map((p) => `${p.category} : ${p.name}`).join(" + ")}</p>
          <div className="grid sm:grid-cols-4 gap-4 text-sm mb-4">
            <div><span className="text-muted-foreground">Score global : </span><b className="text-2xl">{result.globalScore}</b><span className="text-xs">/100</span></div>
            <div><span className="text-muted-foreground">Coût total : </span><b>{euro(result.best.cost)}</b><br /><span className="text-xs text-muted-foreground">sur {euro(result.budget)} disponible</span></div>
            <div><span className="text-muted-foreground">Économie vs combinaison la plus chère : </span><b className="text-emerald-700">{euro(result.savings)}</b></div>
            <div><span className="text-muted-foreground">Reste après achats : </span><b className={(result.budget - result.best.cost) < 0 ? "text-rose-600" : "text-emerald-700"}>{euro(result.budget - result.best.cost)}</b></div>
          </div>

          <div className="bg-white rounded-lg border border-emerald-200 overflow-x-auto mb-3">
            <Table>
              <TableHeader><TableRow><TableHead>Catégorie</TableHead><TableHead>Prestataire</TableHead><TableHead>Prix</TableHead><TableHead>Score individuel</TableHead></TableRow></TableHeader>
              <TableBody>
                {result.best.picks.map((p) => (
                  <TableRow key={p.id} data-testid={`winner-${p.id}`}>
                    <TableCell className="font-medium">{p.category}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{euro(p.price)}</TableCell>
                    <TableCell className="font-mono">{Math.round(p.score)}/100</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Button onClick={applySelection} data-testid="devis-apply" className="bg-emerald-600 hover:bg-emerald-700">Appliquer cette sélection</Button>
        </div>
      )}
    </>
  );
}
